import React, {useEffect, useState} from 'react';
import {Row, Col, Label, Input, Form} from 'reactstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {getProductListActive} from '../../store/Products/actions';
import {saveStockTransfer} from '../../store/StockTransfer/actions';
import {getCurrency, getCompanyConfigurtion} from '../../store/CompanyConfiguration/actions';

const CURRENCY_TYPE = 2.0;

const emptyForm = () => ({
  stockTransferDetailId: 0,
  fromCurrency: null,
  toCurrency: null,
  qty: '',
  baseRate: '',
  convertRate: '',
  errors: {}
});

const StockTransferForm = ({editData, onSaved, onCancel}) => {
  const dispatch = useDispatch();
  const productListResult = useSelector((state) => state.productReducer);
  const configurationData = useSelector((state) => state.companyConfiguration.configurationData);
  const currencyConfig = useSelector((state) => state.companyConfiguration.currency);

  const [date, setDate] = useState(new Date());
  const [dateError, setDateError] = useState('');
  const [form, setForm] = useState(emptyForm());

  const currencyList =
    productListResult?.productList
      ?.filter((a) => a.productType == CURRENCY_TYPE)
      ?.map((a) => ({
        value: a.id,
        label: `${a.productName} (${a.availStock ?? 0})`,
        availStock: a.availStock,
        avgCost: a.avgCost
      })) || [];

  useEffect(() => {
    dispatch(getProductListActive());
    dispatch(getCurrency());
    dispatch(getCompanyConfigurtion());
  }, []);

  const baseCurrencyName = (() => {
    const config = configurationData?.find((c) => c.code === 'BaseCurrency');
    if (!config) return '';
    const match = currencyConfig?.find((c) => c.id == config.value);
    return match?.currencyName || '';
  })();

  useEffect(() => {
    if (editData) {
      setDate(new Date(editData.stockTransferDate));
      setForm({
        stockTransferDetailId: 0,
        fromCurrency: editData.fromCurrencyId ? {value: editData.fromCurrencyId, label: editData.fromCurrency || String(editData.fromCurrencyId)} : null,
        toCurrency: editData.toCurrencyId ? {value: editData.toCurrencyId, label: editData.toCurrency || String(editData.toCurrencyId)} : null,
        qty: editData.qty ?? '',
        baseRate: editData.baseRate ?? '',
        convertRate: editData.convertRate ?? '',
        errors: {}
      });
    } else {
      setDate(new Date());
      setForm(emptyForm());
      setDateError('');
    }
  }, [editData]);

  const sellValue = (() => {
    const q = parseFloat(form.qty) || 0;
    const r = parseFloat(form.baseRate) || 0;
    return q > 0 && r > 0 ? (q * r).toFixed(3) : '';
  })();

  const buyQty = (() => {
    const q = parseFloat(form.qty) || 0;
    const r = parseFloat(form.convertRate) || 0;
    return q > 0 && r > 0 ? (q * r).toFixed(3) : '';
  })();

  const buyRate = (() => {
    const sv = parseFloat(sellValue) || 0;
    const bq = parseFloat(buyQty) || 0;
    return sv > 0 && bq > 0 ? (sv / bq).toFixed(3) : '';
  })();

  const updateField = (field, value) => {
    setForm((prev) => {
      const updated = {...prev, [field]: value, errors: {...prev.errors, [field]: undefined, pairError: undefined}};
      if (field === 'fromCurrency') {
        updated.baseRate = value?.avgCost ?? '';
      }
      const from = field === 'fromCurrency' ? value : updated.fromCurrency;
      const to = field === 'toCurrency' ? value : updated.toCurrency;
      if (from && to && from.value === to.value) {
        updated.errors = {...updated.errors, pairError: 'From Currency and To Currency cannot be the same.'};
      }
      return updated;
    });
  };

  const validate = () => {
    const errors = {};
    if (!form.fromCurrency) errors.fromCurrency = 'Required';
    if (!form.toCurrency) errors.toCurrency = 'Required';
    if (form.errors.pairError) errors.pairError = form.errors.pairError;
if (!form.qty || parseFloat(form.qty) <= 0) errors.qty = 'Required';
if (!form.convertRate || parseFloat(form.convertRate) <= 0) errors.convertRate = 'Required';
    return errors;
  };

  const handleSave = () => {
    let valid = true;
    if (!date) {
      setDateError('Please select a date.');
      valid = false;
    } else {
      setDateError('');
    }
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setForm((prev) => ({...prev, errors}));
      valid = false;
    }
    if (!valid) return;

    const isEdit = !!editData;
    const payload = {
      stockTransferId: editData?.stockTransferId || 0,
      stockTransferDate: moment(date).format('YYYY-MM-DDT00:00:00'),
      fromCurrencyId: form.fromCurrency?.value || 0,
      toCurrencyId: form.toCurrency?.value || 0,
      convertRate: parseFloat(form.convertRate) || 0,
      qty: parseFloat(form.qty) || 0,
      operationToPerform: isEdit ? 2 : 1
    };

    dispatch(saveStockTransfer(payload, onSaved));
  };

  const handleReset = () => {
    setDate(new Date());
    setForm(emptyForm());
    setDateError('');
    onCancel();
  };

  return (
    <div className='card mb-4'>
      <div className='card-body'>
        <h5 className='card-title mb-4'>{editData ? 'Edit' : 'New'} Stock Transfer</h5>
        <Form>
          <Row className='mb-3 align-items-end'>
            <Col md='2'>
              <Label className='col-form-label'>
                Date <span className='text-danger'>*</span>
              </Label>
              <Flatpickr
                className='form-control'
                placeholder='Select Date'
                value={date}
                options={{mode: 'single', disableMobile: true, dateFormat: 'd/m/Y'}}
                onChange={(e) => {
                  setDate(e[0]);
                  setDateError('');
                }}
              />
              {dateError && <div className='text-danger mt-1' style={{fontSize: 12}}>{dateError}</div>}
            </Col>

            <Col md='3'>
              <Label className='col-form-label'>
                From Currency <span className='text-danger'>*</span>
              </Label>
              <Select
                placeholder='Select Currency'
                options={currencyList}
                value={form.fromCurrency}
                onChange={(val) => updateField('fromCurrency', val)}
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({...base, zIndex: 9999}),
                  control: (base) => ({...base, borderColor: form.errors.fromCurrency ? '#f46a6a' : base.borderColor})
                }}
              />
              {form.errors.fromCurrency && <div className='text-danger mt-1' style={{fontSize: 12}}>{form.errors.fromCurrency}</div>}
            </Col>

            <Col md='2'>
              <Label className='col-form-label'>
                Sell Qty <span className='text-danger'>*</span>
              </Label>
              <Input
                type='number'
                className={`form-control${form.errors.qty ? ' is-invalid' : ''}`}
                value={form.qty}
                placeholder='0'
                min='0'
                onChange={(e) => updateField('qty', e.target.value)}
              />
              {form.errors.qty && <div className='invalid-feedback'>{form.errors.qty}</div>}
            </Col>

            <Col md='3'>
              <Label className='col-form-label'>
                To Currency<span className='text-danger'>*</span>
              </Label>
              <Select
                placeholder='Select Currency'
                options={currencyList}
                value={form.toCurrency}
                onChange={(val) => updateField('toCurrency', val)}
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({...base, zIndex: 9999}),
                  control: (base) => ({...base, borderColor: form.errors.toCurrency ? '#f46a6a' : base.borderColor})
                }}
              />
              {form.errors.toCurrency && <div className='text-danger mt-1' style={{fontSize: 12}}>{form.errors.toCurrency}</div>}
              {form.errors.pairError && <div className='text-danger mt-1' style={{fontSize: 12}}>{form.errors.pairError}</div>}
            </Col>

            <Col md='2'>
              <Label className='col-form-label'>
                Exchange Rate <span className='text-danger'>*</span>
              </Label>
              <Input
                type='number'
                className={`form-control${form.errors.convertRate ? ' is-invalid' : ''}`}
                value={form.convertRate}
                placeholder='0.000'
                min='0'
                onChange={(e) => updateField('convertRate', e.target.value)}
              />
              {form.errors.convertRate && <div className='invalid-feedback'>{form.errors.convertRate}</div>}
            </Col>
          </Row>


          <div className='d-flex gap-2'>
             <button type='button' className='btn btn-secondary px-4' onClick={handleReset}>
              {editData ? 'Cancel' : 'Back'}
            </button>
            <button type='button' className='btn btn-primary px-4' onClick={handleSave}>
              {editData ? 'Update' : 'Save'} 
            </button>
           
          </div>
        </Form>
      </div>
    </div>
  );
};

export default StockTransferForm;
